<template>
  <DefaultBackground>
    <div class="container mx-auto mt-12">
      <div class="grid grid-cols-12 gap-6 mx-auto px-4">
        <div class="col-span-12 text-3xl font-semibold">
          Buildabox Caterers Directory
        </div>

        <!-- Left Panel -->
        <div class="col-span-12 lg:col-span-4 xl:col-span-3">

          <!-- widget -->
          <div class="bg-white rounded-lg shadow-card mx-auto px-8 py-12 mb-6">
            <div class="text-2xl font-semibold">Location By State</div>

            <div class="mt-4" v-for="state in states" :key="state">
              <div class="flex items-center">
                <custom-check-box
                  @change="
                    checkboxState => statesFilterChanged(state, checkboxState)
                  "
                />
                <span class="ml-2">{{ state }}</span>
              </div>
            </div>
          </div>
          <!-- # # # -->

          <!-- widget -->
          <div class="bg-white rounded-lg shadow-card mx-auto px-8 py-12 mb-6">
            <div class="text-2xl font-semibold">Cuisine Type</div>

            <div class="mt-4" v-for="ctype in cuisineTypes" :key="ctype.id">
              <div class="flex items-center">
                <custom-check-box
                  @change="state => cuisineTypesFilterChanged(ctype.id, state)"
                />
                <span class="ml-2">{{ ctype.name }}</span>
              </div>
            </div>
          </div>
          <!-- # # # -->
          
        </div>

        <!-- Right Panel -->
        <div class="col-span-12 lg:col-span-8 xl:col-span-9">
          <div class="bg-white rounded-lg shadow-card mx-auto px-8 py-12 mb-6">
            <!-- search -->
            <div class="block justify-between md:flex gap-4">
              <input
                type="text"
                placeholder="Enter Service Area (Postal Code - Exa: 90210)"
                class="col-span-9 w-full shadow border border-gray-200 p-4 rounded hover:shadow-md"
                v-model="filter.location"
                @keyup.enter="loadData"
              />

              <button
                class="block btn btn-primary font-semibold rounded-lg px-10 min-w-10 shadow-md hover:shadow-lg mx-auto mt-4 md:mt-0"
                :disabled="filter.location.length < 3"
                :title="
                  filter.location.length < 3 ? 'Provide at least 3 symbols' : ''
                "
                @click="loadData"
              >
                Search
              </button>
            </div>

            <!-- results -->
            <div class="mt-4" v-if="loading">Loading, please wait...</div>
            <section v-else>
              <div class="mt-8 text-gray-600">
                Showing {{ caterersCount }} results
              </div>

              <!-- caterer items -->
              <router-link
                :to="'/caterers/' + caterer.slug"
                v-for="caterer in caterers"
                :key="caterer.id"
                class="grid grid-cols-12 rounded mt-8 border bg-yellow-50 cursor-pointer hover:shadow"
              >
                <div
                  class="img col-span-12 sm:col-span-3 relative h-40 sm:h-auto"
                >
                  <img
                    class="object-contain h-full w-full absolute inset-0"
                    v-if="caterer.logo"
                    :src="caterer.logo"
                    :alt="caterer.name"
                  />
                  <CatererLogoPlaceholder v-else :name="caterer.name" />
                </div>
                <div class="content col-span-12 sm:col-span-9 p-6">
                  <div class="text-lg w-full">{{ caterer.name }}</div>
                  <div class="text-xs mt-2">
                    {{ caterer.description }}
                  </div>
                  <div
                    class="flex flex-col sm:flex-row flex-wrap justify-between mt-2"
                  >
                    <div class="font-medium text-xs">
                      <span class="mr-8 block md:inline"
                        >{{ caterer.city }}, {{ caterer.state }}</span
                      >
                      <span>{{ caterer.address }}</span>
                    </div>

                    <div class="font-medium text-xs">
                      {{ caterer.phone }}
                    </div>
                  </div>
                </div>
              </router-link>
              <!-- ### -->

              <!-- pagination -->
              <Pagination
                class="mt-8"
                :withoutPaddings="true"
                :meta="paginationMeta"
                @nextPage="paginate"
                @prevPage="paginate"
                @toPage="paginate"
                @perPage="paginate"
              />
            </section>
          </div>
        </div>
      </div>
    </div>
  </DefaultBackground>
</template>

<script>
import DefaultBackground from "@/components/common/layout/DefaultBackground";
import CustomCheckBox from "@/components/CustomCheckBox";
import ApiV1 from "@/api/api_v1.0";
import Pagination from "@/components/pagination/Index";
import CatererLogoPlaceholder from "@/components/caterer/CatererLogoPlaceholder";


export default {
  name: "CaterersListing",

  components: {
    DefaultBackground,
    CustomCheckBox,
    Pagination,
    CatererLogoPlaceholder,
  },

  data() {
    return {
      loading: true,
      caterers: null,
      cuisineTypes: [],
      states: [],
      paginationMeta: null,

      filter: {
        cuisineTypes: [],
        states: [],
        location: ""
      }
    };
  },

  async created() {
    const responseCT = await ApiV1.getAvailableCuisineTypes();
    this.cuisineTypes = responseCT.data;

    const responseST = await ApiV1.getCatererAvailableStates();
    this.states = responseST.data;

    this.loadData();
  },

  methods: {
    paginate(page, perPage) {
      this.$router.push({
        path: "caterers",
        query: {
          items_per_page: perPage,
          page: page
        }
      });

      this.loadData();
    },

    async loadData() {
      this.loading = true;
      const pageNumber = this.$route.query.page;
      const itemsPerPage = this.$route.query.items_per_page;

      let requestData = {
        params: {
          page: pageNumber,
          items_per_page: itemsPerPage
        }
      };

      // check if filters is available and send
      if (this.filter.cuisineTypes.length > 0) {
        requestData.params.cuisine_types = this.filter.cuisineTypes;
      }

      if (this.filter.location > 0) {
        requestData.params.location = this.filter.location;
      }

      if (this.filter.states.length > 0) {
        requestData.params.states = this.filter.states;
      }

      const response = await ApiV1.getCaterers(requestData);

      this.caterers = response.data.data;
      this.paginationMeta = response.data.meta;

      this.loading = false;
    },

    cuisineTypesFilterChanged(id, state) {
      if (state && this.filter.cuisineTypes.indexOf(id) < 0) {
        this.filter.cuisineTypes.push(id);
      }

      if (!state && this.filter.cuisineTypes.indexOf(id) > -1) {
        this.filter.cuisineTypes.splice(
          this.filter.cuisineTypes.indexOf(id),
          1
        );
      }

      this.loadData();
    },

    statesFilterChanged(value, state) {
      if (state && this.filter.states.indexOf(value) < 0) {
        this.filter.states.push(value);
      }

      if (!state && this.filter.states.indexOf(value) > -1) {
        this.filter.states.splice(this.filter.states.indexOf(value), 1);
      }

      this.loadData();
    }
  },

  computed: {
    caterersCount() {
      return this.paginationMeta ? this.paginationMeta.total : 0;
    }
  }
};
</script>
